import React, { useState, useEffect, useCallback } from 'react';
import { auth, functions } from '../firebase/config';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import BiometricSettings from '../components/BiometricSettings';
import Header from '../components/Header';
import { httpsCallable } from 'firebase/functions';
import { platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';

const ChangePasswordModal = ({ isOpen, onClose, onChangePassword }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError("New passwords do not match.");
            return;
        }
        if (newPassword.length < 6) {
            setError("New password must be at least 6 characters long.");
            return;
        }
        setError('');
        onChangePassword(currentPassword, newPassword);
    };

    const PasswordInput = ({ label, value, setter, show, toggler }) => (
        <div className="relative">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <input type={show ? 'text' : 'password'} value={value} onChange={(e) => setter(e.target.value)} className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" required />
            <button type="button" onClick={toggler} className="absolute inset-y-0 right-0 top-6 pr-3 flex items-center text-sm leading-5">
                {show ? <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a9.97 9.97 0 01-1.563 3.029m-5.858-.908a3 3 0 00-4.243-4.243"/></svg> : <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.522 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.478 0-8.268-2.943-9.542-7z" /></svg>}
            </button>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Change Password</h3>
                {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <PasswordInput label="Current Password" value={currentPassword} setter={setCurrentPassword} show={showCurrent} toggler={() => setShowCurrent(!showCurrent)} />
                    <PasswordInput label="New Password" value={newPassword} setter={setNewPassword} show={showNew} toggler={() => setShowNew(!showNew)} />
                    <PasswordInput label="Confirm New Password" value={confirmPassword} setter={setConfirmPassword} show={showConfirm} toggler={() => setShowConfirm(!showConfirm)} />
                    <div className="flex justify-end space-x-3 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Change Password</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const countryDateFormatMap = {
    'Australia': 'DD/MM/YYYY',
    'United Kingdom': 'DD/MM/YYYY',
    'United States': 'MM/DD/YYYY',
    'Canada': 'YYYY/MM/DD',
};

const ProfilePage = ({ navigateTo, currentPage }) => {
    const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [displayName, setDisplayName] = useState('');
    const [country, setCountry] = useState('');
    const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
    const [profileError, setProfileError] = useState('');
    const [profileSuccess, setProfileSuccess] = useState('');
    const [isProfileLoading, setIsProfileLoading] = useState(true);
    const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

    const fetchUserProfile = useCallback(async () => {
        setIsProfileLoading(true);
        try {
            const getUserProfile = httpsCallable(functions, 'getUserProfile');
            const result = await getUserProfile();
            const { displayName, country, dateFormat } = result.data;
            setDisplayName(displayName || auth.currentUser.displayName || '');
            setCountry(country || 'Australia');
            setDateFormat(dateFormat || countryDateFormatMap[country || 'Australia']);
            const checkAdminStatus = httpsCallable(functions, 'isSuperAdmin');
            const adminResult = await checkAdminStatus();
            setIsCurrentUserAdmin(adminResult.data);
        } catch (error) {
            console.error("Error fetching profile:", error);
            setProfileError(error.message);
        } finally {
            setIsProfileLoading(false);
        }
    }, []);

    useEffect(() => {
        platformAuthenticatorIsAvailable().then(setIsBiometricAvailable);
        fetchUserProfile();
    }, [fetchUserProfile]);

    const handleCountryChange = (newCountry) => {
        const oldDefaultFormat = countryDateFormatMap[country];
        const newDefaultFormat = countryDateFormatMap[newCountry];
        setCountry(newCountry);
        if (dateFormat === oldDefaultFormat) {
            setDateFormat(newDefaultFormat);
        }
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setIsProfileLoading(true);
        setProfileError('');
        setProfileSuccess('');
        try {
            const updateUserProfile = httpsCallable(functions, 'updateUserProfile');
            await updateUserProfile({ displayName, country, dateFormat });
            setProfileSuccess("Profile saved successfully!");
            setTimeout(() => setProfileSuccess(''), 3000);
        } catch (error) {
            console.error("Error saving profile:", error);
            setProfileError(error.message);
        } finally {
            setIsProfileLoading(false);
        }
    };

    const handleChangePassword = async (currentPassword, newPassword) => {
        setProfileError('');
        setProfileSuccess('');
        const user = auth.currentUser;
        const credential = EmailAuthProvider.credential(user.email, currentPassword);

        try {
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            
            const sendNotification = httpsCallable(functions, 'sendPasswordChangedNotification');
            await sendNotification();

            setProfileSuccess("Password changed successfully!");
            setIsChangePasswordOpen(false);
            setTimeout(() => setProfileSuccess(''), 3000);
        } catch (error) {
            console.error("Error changing password:", error);
            setProfileError(error.message);
        }
    };

    const handleRegisterBiometric = async () => {
        try {
            const deviceName = prompt("Please enter a name for this device:", "My Device");
            if (!deviceName) return;
            const getRegistrationOptions = httpsCallable(functions, 'getRegistrationOptions');
            const options = await getRegistrationOptions();
            const { startRegistration } = await import('@simplewebauthn/browser');
            const attResp = await startRegistration(options.data);
            const verifyRegistration = httpsCallable(functions, 'verifyRegistration');
            const result = await verifyRegistration({ ...attResp, deviceName });
            if (result.data.verified) {
                alert("Biometrics registered successfully!");
                setRefreshTrigger(t => t + 1);
            } else {
                alert("Failed to register biometrics.");
            }
        } catch (error) {
            alert(`Error: ${error.message}`);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-8">
            <Header title="My Profile" navigateTo={navigateTo} currentPage={currentPage} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                 <button onClick={() => navigateTo('family')} className="w-full p-3 bg-indigo-500 text-white rounded-md hover:bg-indigo-600">Manage Family</button>
                 <button onClick={() => navigateTo('categories')} className="w-full p-3 bg-teal-500 text-white rounded-md hover:bg-teal-600">Manage Categories</button>
                 {isCurrentUserAdmin && <button onClick={() => navigateTo('superAdmin')} className="w-full p-3 bg-purple-500 text-white rounded-md hover:bg-purple-600">Super Admin</button>}
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h2 className="text-xl font-semibold mb-4">Your Details</h2>
                {profileError && <p className="text-red-500 text-sm mb-4">{profileError}</p>}
                {profileSuccess && <p className="text-green-500 text-sm mb-4">{profileSuccess}</p>}
                {isProfileLoading ? <p>Loading profile...</p> : (
                    <form onSubmit={handleSaveProfile} className="space-y-4">
                        <div>
                            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">Display Name</label>
                            <input type="text" id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
                        </div>
                        <div>
                            <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
                            <select id="country" value={country} onChange={(e) => handleCountryChange(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 rounded-md">
                                <option>Australia</option><option>United Kingdom</option><option>United States</option><option>Canada</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700">Date Format</label>
                            <select id="dateFormat" value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 rounded-md">
                                <option>DD/MM/YYYY</option><option>MM/DD/YYYY</option><option>YYYY/MM/DD</option>
                            </select>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                            <button type="button" onClick={() => setIsChangePasswordOpen(true)} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">Change Password</button>
                            <button type="submit" disabled={isProfileLoading} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300">
                                {isProfileLoading ? 'Saving...' : 'Save Profile'}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {isBiometricAvailable && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <h2 className="text-xl font-semibold mb-4">Register New Biometric Login</h2>
                    <p className="text-gray-600 mb-4">Click the button below to register this device for passwordless sign-in.</p>
                    <button onClick={handleRegisterBiometric} className="px-5 py-2 font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">Register This Device</button>
                </div>
            )}
            
            {isBiometricAvailable && <BiometricSettings functions={functions} refreshTrigger={refreshTrigger} />}
            <ChangePasswordModal isOpen={isChangePasswordOpen} onClose={() => setIsChangePasswordOpen(false)} onChangePassword={handleChangePassword} />
        </div>
    );
};

export default ProfilePage;